// @ts-nocheck
/*
 * Port of views/dashboard/dashboard-calls-charts.js.
 * Zero-API — pure presentation. Every prop is threaded through from parent
 * DashboardPage byte-identical. Chart libraries preserved (react-apexcharts +
 * recharts). Shell rebuilt on shadcn Card / Input / Button + Tailwind.
 * Table column count preserved: 6 columns (Period, Total, Answered, Answered %,
 * Unanswered, Unanswered %).
 */
import { useMemo, useState } from 'react';
import { BarChart3, PieChart as PieChartIcon, Phone, RefreshCw } from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import Chart from 'react-apexcharts';
import type { ColumnDef } from '@tanstack/react-table';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/data-table/DataTable';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

function dateToInputStr(d) {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function inputStrToLocalDate(str) {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!m) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
}

const AGENT_COLORS = ['#6366f1', '#f43f5e', '#0ea5e9'];
const INCOMING_COLORS = ['#f97316', '#10b981', '#a855f7'];
const OUTGOING_COLORS = ['#ef4444', '#eab308', '#22c55e'];

function LegendDot({ color }) {
  return (
    <span
      className="inline-block h-2 w-2 shrink-0 rounded-full"
      style={{ background: color }}
    />
  );
}

function LegendRow({ items }) {
  return (
    <div className="mb-2 flex flex-wrap gap-x-3.5 gap-y-1.5">
      {items.map(({ color, label }) => (
        <span
          key={label}
          className="flex items-center gap-1.5 text-[11px] font-medium tracking-tight text-muted-foreground"
        >
          <LegendDot color={color} />
          {label}
        </span>
      ))}
    </div>
  );
}

function ToggleBtn({ isBar, onToggle }) {
  const Icon = isBar ? PieChartIcon : BarChart3;
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      onClick={onToggle}
      title={isBar ? 'Switch to donut view' : 'Switch to bar view'}
      className="h-8 w-8 shrink-0 rounded-full border-border text-primary"
    >
      <Icon className="h-3.5 w-3.5" />
    </Button>
  );
}

function EmptyState({ label }) {
  return (
    <div className="flex h-full min-h-[120px] flex-col items-center justify-center gap-2.5">
      <div
        className="relative flex h-[88px] w-[88px] items-center justify-center rounded-full"
        style={{
          background:
            'conic-gradient(rgba(99,102,241,0.18) 0% 40%, rgba(148,163,184,0.12) 40% 70%, rgba(99,102,241,0.10) 70% 100%)',
        }}
      >
        <div className="flex h-[52px] w-[52px] items-center justify-center rounded-full bg-card text-muted-foreground">
          <Phone className="h-4 w-4 opacity-40" />
        </div>
      </div>
      <span className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

function ChartCard({ title, isBar, onToggle, legend, children }) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-bold tracking-tight text-foreground">{title}</span>
        <ToggleBtn isBar={isBar} onToggle={onToggle} />
      </div>
      {legend}
      <div className="flex flex-1 items-center justify-center">{children}</div>
    </div>
  );
}

const donutProps = {
  cx: '50%',
  cy: '50%',
  innerRadius: 38,
  outerRadius: 60,
  paddingAngle: 3,
  dataKey: 'value',
  nameKey: 'name',
};

export default function DashboardCallsCharts({
  barAgentsData,
  doughnutData1,
  barIncomingData,
  incomingData,
  isIncomingDataEmpty,
  barOutgoingData,
  outgoingDataDoughnut,
  isOutgoingDataEmpty,
  startDate,
  endDate,
  handleDashboardStartDateChange,
  handleDashboardEndDateChange,
  dashboardStartCalendarMinDate,
  dashboardStartCalendarMaxDate,
  dashboardEndCalendarMinDate,
  dashboardEndCalendarMaxDate,
  handleFilter,
  onRefreshCalls,
  answeredUnansweredSummary = [],
  answeredUnansweredSummaryLoading = false,
}) {
  const [ToggleincomingBarChart, setToggleincomingBarChart] = useState(false);
  const [ToggleOutgoingBarChart, setToggleOutgoingBarChart] = useState(false);
  const [ToggleAgentBarChart, setToggleAgentBarChart] = useState(false);
  const [ToggleAgentPieChart, setToggleAgentPieChart] = useState(true);
  const answeredUnansweredRows = Array.isArray(answeredUnansweredSummary)
    ? answeredUnansweredSummary
    : [];
  const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');
  const formatPercentage = (value) => {
    const numeric = Number(value || 0);
    return `${numeric.toFixed(2).replace(/\.00$/, '')}%`;
  };

  const answeredUnansweredColumns = useMemo<ColumnDef<any>[]>(
    () => [
      { accessorKey: 'label', header: 'Period', cell: ({ row }) => <span className="font-bold">{row.original.label}</span> },
      { accessorKey: 'total_calls', header: 'Total', cell: ({ row }) => formatNumber(row.original.total_calls) },
      {
        accessorKey: 'answered_calls',
        header: 'Answered',
        cell: ({ row }) => (
          <span className="font-bold" style={{ color: '#10b981' }}>{formatNumber(row.original.answered_calls)}</span>
        ),
      },
      { accessorKey: 'answered_percentage', header: 'Answered %', cell: ({ row }) => formatPercentage(row.original.answered_percentage) },
      {
        accessorKey: 'unanswered_calls',
        header: 'Unanswered',
        cell: ({ row }) => (
          <span className="font-bold" style={{ color: '#f43f5e' }}>{formatNumber(row.original.unanswered_calls)}</span>
        ),
      },
      { accessorKey: 'unanswered_percentage', header: 'Unanswered %', cell: ({ row }) => formatPercentage(row.original.unanswered_percentage) },
    ],
    [],
  );

  const tooltipStyle = {
    background: 'hsl(var(--card))',
    border: '1px solid hsl(var(--border))',
    borderRadius: 10,
    color: 'hsl(var(--foreground))',
    fontSize: 12,
  };

  return (
    <div className="mb-4 grid w-full grid-cols-1 gap-4 overflow-x-hidden md:grid-cols-[280px_1fr]">
      {/* Agents card */}
      <Card className="p-4">
        <ChartCard
          title="Agents"
          isBar={ToggleAgentBarChart}
          onToggle={() => {
            setToggleAgentBarChart((prev) => !prev);
            setToggleAgentPieChart((prev) => !prev);
          }}
          legend={
            <LegendRow
              items={[
                { color: AGENT_COLORS[0], label: 'Total' },
                { color: AGENT_COLORS[1], label: 'Logged in' },
                { color: AGENT_COLORS[2], label: 'On call' },
              ]}
            />
          }
        >
          <div className="w-full">
            {ToggleAgentBarChart ? (
              <Chart
                options={barAgentsData.options}
                series={barAgentsData.series}
                type="bar"
                height={220}
                width="100%"
              />
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie {...donutProps} data={doughnutData1}>
                    {doughnutData1.map((_, idx) => (
                      <Cell key={`agent-${idx}`} fill={AGENT_COLORS[idx % AGENT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </ChartCard>
      </Card>

      {/* Calls card */}
      <Card className="p-4">
        <div className="mb-2.5 flex items-center justify-between">
          <span className="text-sm font-bold tracking-tight text-foreground">
            Agent Incoming &amp; Outgoing
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={onRefreshCalls}
            title="Refresh Incoming & Outgoing"
            aria-label="Refresh Incoming & Outgoing"
            className="h-8 w-8 rounded-full"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Date filter toolbar */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <CalendarDateInput
            value={dateToInputStr(startDate)}
            onChange={(e) =>
              handleDashboardStartDateChange({
                value: inputStrToLocalDate(e),
              })
            }
            min={dateToInputStr(dashboardStartCalendarMinDate)}
            max={dateToInputStr(dashboardStartCalendarMaxDate)}
            placeholder="Start Date"
            className="h-9 w-40"
          />
          <span className="shrink-0 text-xs font-semibold text-muted-foreground">to</span>
          <CalendarDateInput
            value={dateToInputStr(endDate)}
            onChange={(e) =>
              handleDashboardEndDateChange({
                value: inputStrToLocalDate(e),
              })
            }
            min={dateToInputStr(dashboardEndCalendarMinDate)}
            max={dateToInputStr(dashboardEndCalendarMaxDate)}
            placeholder="End Date"
            className="h-9 w-40"
          />
          <Button onClick={handleFilter} size="sm">
            Filter
          </Button>
        </div>

        {/* Incoming + Outgoing */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ChartCard
            title="Incoming Calls"
            isBar={ToggleincomingBarChart}
            onToggle={() => setToggleincomingBarChart((prev) => !prev)}
            legend={
              <LegendRow
                items={[
                  { color: INCOMING_COLORS[0], label: 'Dialed' },
                  { color: INCOMING_COLORS[1], label: 'Answered' },
                  { color: INCOMING_COLORS[2], label: 'Abandoned' },
                ]}
              />
            }
          >
            <div className="h-[180px] w-full">
              {ToggleincomingBarChart ? (
                <Chart
                  options={barIncomingData.options}
                  series={barIncomingData.series}
                  type="bar"
                  height={200}
                  width="100%"
                />
              ) : isIncomingDataEmpty ? (
                <EmptyState label="No incoming calls" />
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie {...donutProps} data={incomingData}>
                      {incomingData.map((_, idx) => (
                        <Cell
                          key={`inc-${idx}`}
                          fill={INCOMING_COLORS[idx % INCOMING_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </ChartCard>

          <ChartCard
            title="Outgoing Calls"
            isBar={ToggleOutgoingBarChart}
            onToggle={() => setToggleOutgoingBarChart((prev) => !prev)}
            legend={
              <LegendRow
                items={[
                  { color: OUTGOING_COLORS[0], label: 'Total' },
                  { color: OUTGOING_COLORS[1], label: 'Dialed' },
                  { color: OUTGOING_COLORS[2], label: 'Answered' },
                ]}
              />
            }
          >
            <div className="h-[180px] w-full">
              {ToggleOutgoingBarChart ? (
                <Chart
                  options={barOutgoingData.options}
                  series={barOutgoingData.series}
                  type="bar"
                  height={200}
                  width="100%"
                />
              ) : isOutgoingDataEmpty ? (
                <EmptyState label="No outgoing calls" />
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie {...donutProps} data={outgoingDataDoughnut}>
                      {outgoingDataDoughnut.map((_, idx) => (
                        <Cell
                          key={`out-${idx}`}
                          fill={OUTGOING_COLORS[idx % OUTGOING_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </ChartCard>
        </div>

        {/* Answered vs Unanswered — 6 columns via obd3-gui DataTable */}
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-sm font-bold text-foreground">Answered vs Unanswered Calls</span>
            <span className="text-[11px] font-semibold text-muted-foreground">
              Today, yesterday, this week, this month, last month
            </span>
          </div>
          <DataTable
            tableId="dashboard.answered-unanswered-summary"
            columns={answeredUnansweredColumns}
            data={answeredUnansweredRows}
            isLoading={answeredUnansweredSummaryLoading}
            emptyTitle="No call summary"
            emptyDescription="No call summary available."
            pageSize={10}
            exportFilename="answered-vs-unanswered.csv"
          />
        </div>
      </Card>
    </div>
  );
}
